import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import AttendanceShowcase from "../sections/AttendanceShowcase";
import PayrollShowcase from "../sections/PayrollShowcase";
import dashboardHeroImage from "../../../assets/dashboard-preview.png";
import {
  Users,
  Clock,
  Calendar,
  TrendingUp,
  Receipt,
  Handshake,
  ArrowRight
} from "lucide-react";
import "../../Website/styles/website.css";
import "../../Website/styles/ProductPages.css";

const GTELINQ = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [activeTab, setActiveTab] = useState("leave");

  // Simulations states
  const [leaveState, setLeaveState] = useState("idle"); // 'idle', 'submitting', 'approved'
  const [leaveBalance, setLeaveBalance] = useState(12);
  const [attendanceState, setAttendanceState] = useState("idle"); // 'idle', 'locating', 'success'
  const [meetingStep, setMeetingStep] = useState("schedule"); // 'schedule', 'sending', 'received', 'completed'
  const [payrollStep, setPayrollStep] = useState("upload"); // 'upload', 'publishing', 'received', 'viewing'

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Reset states so they can click and watch the animations again
    if (tab === "leave") {
      setLeaveState("idle");
      setLeaveBalance(12);
    } else if (tab === "attendance") {
      setAttendanceState("idle");
    } else if (tab === "payroll") {
      setPayrollStep("upload");
    } else if (tab === "performance") {
      setMeetingStep("schedule");
    }
  };

  const startAttendanceSimulation = () => {
    if (attendanceState !== "idle") return;
    setAttendanceState("locating");
    setTimeout(() => {
      setAttendanceState("success");
    }, 2000);
  };

  const startLeaveSimulation = () => {
    if (leaveState !== "idle") return;
    setLeaveState("submitting");
    setTimeout(() => {
      setLeaveState("approved");
      setLeaveBalance(9);
    }, 2000);
  };

  const startPayrollSimulation = () => {
    if (payrollStep !== "upload") return;
    setPayrollStep("publishing");
    setTimeout(() => {
      setPayrollStep("received");
    }, 2000);
  };

  const startMeetingSimulation = () => {
    if (meetingStep !== "schedule") return;
    setMeetingStep("sending");
    setTimeout(() => {
      setMeetingStep("received");
    }, 2000);
  };

  const isMobile = screenWidth < 768;
  const youtubeEmbedUrl =
    "https://www.youtube.com/embed/cu4SwU5JeAA?autoplay=1&rel=0&modestbranding=1&playsinline=1";

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "Attendance Management",
      description: "Track employee attendance, shifts, punch-ins, and working hours in real-time.",
      icon: <Clock size={22} />
    },
    {
      title: "Pay & Benefits",
      description: "Manage monthly salary documents, payslips, and HR documents from one centralized module.",
      icon: <Calendar size={22} />
    },
    {
      title: "Leave Management",
      description: "Manage leave requests, approvals, balances, and holiday calendars seamlessly.",
      icon: <TrendingUp size={22} />
    },
    {
      title: "Employee Management",
      description: "Manage employee profiles, records, departments, and organizational information from a centralized platform..",
      icon: <Receipt size={22} />
    },
    {
      title: "One-on-One Meetings",
      description: "Plan meetings, assign tasks, record discussion notes, and notify employees of missed sessions through automated emails.",
      icon: <Users size={22} />
    },
    {
      title: "Performance Tracking",
      description: "Monitor OKR's, goals, reviews, and employee performance analytics efficiently.",
      icon: <Handshake size={22} />
    }
  ];

  // Video handles removed for inline homepage style

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow" style={{ top: "40%", left: "50%", transform: "translate(-50%, -50%)", width: "600px", height: "600px", opacity: 0.15 }}></div>

        <div className="prod-hero-left">
          <span className="prod-badge">HRMS SOFTWARE</span>
          <h1 className="prod-main-title">
            GTELINQ HRMS. <br />
            <span style={{ color: "#55FF60" }}>Modern Workforce.</span>
          </h1>
          <p className="prod-desc">
            Align employee records, automate attendance tracking, manage leaves,
            and streamline appraisals in a single premium portal.
          </p>
          <div className="prod-cta-group">
            <button
              onClick={() => setShowVideo(true)}
              className="prod-primary-btn"
              style={{ border: "none", cursor: "pointer", padding: "16px 36px", fontSize: "16px", borderRadius: "999px" }}
            >
              Watch Demo <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right">
          <div className="prod-mockup-frame" style={{
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            padding: isMobile ? "8px" : "12px",
            background: "rgba(10, 10, 10, 0.4)",
            borderColor: "rgba(85, 255, 96, 0.2)",
            boxShadow: "0 25px 80px rgba(85, 255, 96, 0.12)",
            backdropFilter: "blur(20px)"
          }}>
            <div className="mockup-header" style={{ paddingBottom: "8px", marginBottom: "6px" }}>
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">hrms.gtelinq.com/dashboard</span>
            </div>
            <div className="mockup-content" style={{ display: "block", overflow: "hidden", padding: 0 }}>
              <img
                src={dashboardHeroImage}
                alt="GTELINQ HRMS Dashboard"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: "0 0 12px 12px",
                  imageRendering: "-webkit-optimize-contrast"
                }}
              />
            </div>
          </div>
        </div>

        {/* DETAILED SPECIFICATIONS ROW (Quick Info about HRMS) */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: "30px",
          width: "100%",
          maxWidth: "1000px",
          marginTop: "20px",
          zIndex: 10
        }}>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "24px",
            borderRadius: "16px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#55FF60", fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>✓ Smart Compliance</h4>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.6" }}>
              Automatically calculates tax deductions, local labor compliance laws, and custom provident fund options dynamically.
            </p>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "24px",
            borderRadius: "16px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#55FF60", fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>✓ Live Geo-Tracking</h4>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.6" }}>
              Geofence office regions for mobile check-ins and verify remote attendance requests with high-accuracy GPS coordinates.
            </p>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            padding: "24px",
            borderRadius: "16px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#55FF60", fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>✓ Unified Directory</h4>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", lineHeight: "1.6" }}>
              One central portal for team profiles, reporting structures, files, contracts, and internal organization hierarchies.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">CORE HR CAPABILITIES</span>
          <h2 className="prod-section-title">Automate Employee Lifecycles</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DETAILED SHOWCASE SECTIONS */}
      <AttendanceShowcase />
      <PayrollShowcase />

      {/* INTERACTIVE MODULE SIMULATOR */}
      <section className="prod-features-section" style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "80px", background: "#000" }}>
        <style dangerouslySetInnerHTML={{
          __html: `
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes loadingBar {
            from { width: 0%; }
            to { width: 100%; }
          }
          @keyframes radarPulse {
            0% { transform: scale(0.6); opacity: 1; }
            100% { transform: scale(1.4); opacity: 0; }
          }
        `}} />

        <div className="prod-section-header" style={{ marginBottom: "50px" }}>
          <span className="prod-section-mini">LIVE DEMO WORKSPACE</span>
          <h2 className="prod-section-title">Interactive Module Workspace</h2>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", marginTop: "12px" }}>
            Click on any module to run live workspace operations and experience GTELINQ's real-time automation.
          </p>
        </div>

        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "40px",
          maxWidth: "1200px",
          margin: "0 auto",
          background: "rgba(255,255,255,0.01)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: "24px",
          padding: isMobile ? "20px" : "40px",
          boxShadow: "0 30px 60px rgba(0,0,0,0.8)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
          position: "relative"
        }}>
          {/* Left Navigation Tabs */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            flex: "1",
            justifyContent: "center"
          }}>
            {[
              { id: "leave", title: "Leave Management", desc: "Auto-accrual & manager approvals", icon: <Calendar size={20} /> },
              { id: "attendance", title: "Attendance Geofencing", desc: "Live GPS punch-in validation", icon: <Clock size={20} /> },
              { id: "performance", title: "One-on-One Meetings", desc: "Discussion notes & task tracking", icon: <Handshake size={20} /> },
              { id: "payroll", title: "Pay & Benefits", desc: "Instant digital payslip generator", icon: <Receipt size={20} /> }
            ].map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "20px",
                    background: active ? "rgba(85, 255, 96, 0.08)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${active ? "rgba(85, 255, 96, 0.25)" : "rgba(255,255,255,0.05)"}`,
                    borderRadius: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                    width: "100%",
                    outline: "none"
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: active ? "#55FF60" : "rgba(255,255,255,0.04)",
                    color: active ? "#000" : "#55FF60",
                    transition: "all 0.3s ease"
                  }}>
                    {tab.icon}
                  </div>
                  <div>
                    <h4 style={{ color: active ? "#fff" : "rgba(255,255,255,0.8)", margin: "0 0 4px", fontSize: "15px", fontWeight: "700" }}>{tab.title}</h4>
                    <p style={{ color: active ? "#55FF60" : "rgba(255,255,255,0.4)", margin: 0, fontSize: "12px" }}>{tab.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Simulated Screen */}
          <div style={{
            flex: "1.8",
            background: "rgba(10, 10, 10, 0.95)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "20px",
            minHeight: "350px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
            boxShadow: "inset 0 0 30px rgba(0,0,0,0.8)"
          }}>
            {/* Mock Screen Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              padding: "12px 20px",
              background: "#111"
            }}>
              <div style={{ display: "flex", gap: "6px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ff5f56" }} />
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ffbd2e" }} />
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#27c93f" }} />
              </div>
              <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", fontWeight: "500" }}>
                gtelinq-workspace://{activeTab}-simulation
              </span>
              <div style={{ width: "30px" }} />
            </div>

            {/* Mock Screen Content View */}
            <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {activeTab === "leave" && (
                <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px", animation: "slideUp 0.3s ease-out" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h4 style={{ color: "#fff", margin: 0, fontSize: "18px", fontWeight: "700" }}>Apply Leave Portal</h4>
                    <div style={{ background: "rgba(85,255,96,0.1)", padding: "6px 12px", borderRadius: "8px", border: "1px solid rgba(85,255,96,0.2)" }}>
                      <span style={{ color: "#55FF60", fontSize: "13px", fontWeight: "600" }}>Remaining Balance: {leaveBalance} Days</span>
                    </div>
                  </div>

                  <div style={{ background: "rgba(255,255,255,0.02)", padding: "16px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.05)", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <span>Leave Type:</span>
                      <span style={{ color: "#fff", fontWeight: "600" }}>Casual Leave</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <span>Duration:</span>
                      <span style={{ color: "#fff", fontWeight: "600" }}>3 Days (Oct 12 - Oct 14)</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <span>Application Status:</span>
                      {leaveState === "idle" && <span style={{ color: "#ffbd2e", fontWeight: "600" }}>Draft</span>}
                      {leaveState === "submitting" && <span style={{ color: "#55ff60", fontWeight: "600", animation: "pulse 1.2s infinite" }}>Submitting to Manager...</span>}
                      {leaveState === "approved" && <span style={{ color: "#55ff60", fontWeight: "600" }}>Approved ✓</span>}
                    </div>
                  </div>

                  {leaveState === "idle" && (
                    <button onClick={startLeaveSimulation} style={{ background: "#55FF60", color: "#000", border: "none", padding: "14px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "14px", transition: "all 0.2s" }}>
                      Submit Leave Request
                    </button>
                  )}
                  {leaveState === "submitting" && (
                    <div style={{ textAlign: "center", padding: "10px", color: "rgba(255,255,255,0.4)", fontSize: "13px" }}>
                      <div style={{ width: "100%", height: "4px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden", marginBottom: "8px" }}>
                        <div style={{ height: "100%", width: "100%", background: "#55FF60", borderRadius: "2px", animation: "loadingBar 2s infinite" }}></div>
                      </div>
                      Processing auto-approval rules...
                    </div>
                  )}
                  {leaveState === "approved" && (
                    <div style={{ background: "rgba(85,255,96,0.05)", border: "1px dashed rgba(85,255,96,0.3)", padding: "16px", borderRadius: "12px", textAlign: "center" }}>
                      <p style={{ color: "#55FF60", fontSize: "14px", margin: "0 0 10px", fontWeight: "600" }}>
                        Instant Approved by Aditi Patil. Leave sheets updated in monthly payroll!
                      </p>
                      <button onClick={() => handleTabChange("leave")} style={{ background: "transparent", color: "rgba(255,255,255,0.5)", border: "none", textDecoration: "underline", fontSize: "12px", cursor: "pointer" }}>
                        Run Simulation Again
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "attendance" && (
                <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px", animation: "slideUp 0.3s ease-out" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h4 style={{ color: "#fff", margin: 0, fontSize: "18px", fontWeight: "700" }}>GPS Geofenced Clock-In</h4>
                    <div style={{ background: "rgba(85,255,96,0.1)", padding: "6px 12px", borderRadius: "8px", border: "1px solid rgba(85,255,96,0.2)" }}>
                      <span style={{ color: "#55FF60", fontSize: "13px", fontWeight: "600" }}>Head Office (100m Geofence)</span>
                    </div>
                  </div>

                  <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "12px", padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <span>Radar Scan GPS:</span>
                      <span style={{ color: "#fff", fontFamily: "monospace" }}>Lat: 28.6139, Lng: 77.2090</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <span>Verified Location:</span>
                      <span style={{ color: "#fff", fontWeight: "600" }}>Sector 62, Noida, IN</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <span>Clock-in Status:</span>
                      {attendanceState === "idle" && <span style={{ color: "#ffbd2e", fontWeight: "600" }}>Awaiting Punch-In</span>}
                      {attendanceState === "locating" && <span style={{ color: "#55ff60", fontWeight: "600", animation: "pulse 1.2s infinite" }}>Verifying GPS location...</span>}
                      {attendanceState === "success" && <span style={{ color: "#55ff60", fontWeight: "600" }}>Clocked In ✓</span>}
                    </div>
                  </div>

                  {attendanceState === "idle" && (
                    <button onClick={startAttendanceSimulation} style={{ background: "#55FF60", color: "#000", border: "none", padding: "14px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "14px", transition: "all 0.2s" }}>
                      Scan Geofence & Punch-In
                    </button>
                  )}
                  {attendanceState === "locating" && (
                    <div style={{ textAlign: "center", padding: "15px", position: "relative" }}>
                      <div style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        border: "2px solid #55FF60",
                        margin: "0 auto 12px",
                        animation: "radarPulse 1.5s infinite ease-out",
                        position: "relative"
                      }}>
                        <div style={{
                          position: "absolute",
                          inset: "15px",
                          borderRadius: "50%",
                          background: "#55FF60",
                          opacity: 0.8
                        }} />
                      </div>
                      <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>Connecting with secure GPS satellites...</span>
                    </div>
                  )}
                  {attendanceState === "success" && (
                    <div style={{ background: "rgba(85,255,96,0.05)", border: "1px dashed rgba(85,255,96,0.3)", padding: "16px", borderRadius: "12px", animation: "slideUp 0.3s ease-out" }}>
                      <p style={{ color: "#55FF60", fontSize: "14px", margin: "0 0 12px", fontWeight: "600", textAlign: "center" }}>
                        ✓ Location matches Head Office geofence (Distance: 12 meters).
                      </p>

                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "rgba(255,255,255,0.5)", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "10px" }}>
                        <span>Punch Time: 09:02 AM</span>
                        <span style={{ color: "#55FF60" }}>On Time</span>
                      </div>

                      <button onClick={() => handleTabChange("attendance")} style={{ display: "block", background: "transparent", color: "rgba(255,255,255,0.5)", border: "none", textDecoration: "underline", fontSize: "12px", margin: "12px auto 0", cursor: "pointer" }}>
                        Reset Punch Simulator
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "performance" && (
                <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px", animation: "slideUp 0.3s ease-out" }}>

                  {/* Step 1 & 2: Management schedules One-on-One */}
                  {(meetingStep === "schedule" || meetingStep === "sending") && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ color: "#fff", margin: 0, fontSize: "16px", fontWeight: "700" }}>Management Portal: Publish One-on-One</h4>
                        <span style={{ fontSize: "12px", color: "#55FF60", fontWeight: "600" }}>Role: Manager</span>
                      </div>

                      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "12px", padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)" }}>Employee:</span>
                          <span style={{ color: "#fff", fontWeight: "600" }}>Siya Sharma</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)" }}>Discussion:</span>
                          <span style={{ color: "#fff", fontWeight: "600" }}>Q4 Roadmap & API Design Sync</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "8px", marginTop: "4px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>Assigned Action Item:</span>
                          <span style={{ color: "#55FF60", fontSize: "13px", fontWeight: "500" }}>📄 Complete database migration plan</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)" }}>Due Date:</span>
                          <span style={{ color: "#ff5f56", fontWeight: "600" }}>Nov 15, 2026</span>
                        </div>
                      </div>

                      {meetingStep === "schedule" ? (
                        <button onClick={startMeetingSimulation} style={{ background: "#55FF60", color: "#000", border: "none", padding: "14px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "14px", transition: "all 0.2s" }}>
                          Publish Summary & Assign Action Items
                        </button>
                      ) : (
                        <div style={{ textAlign: "center", padding: "10px" }}>
                          <div style={{ width: "100%", height: "4px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden", marginBottom: "8px" }}>
                            <div style={{ height: "100%", width: "100%", background: "#55FF60", borderRadius: "2px", animation: "loadingBar 2s infinite" }}></div>
                          </div>
                          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>Syncing discussion notes and dispatching tasks...</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Step 3: Employee Receives Task */}
                  {meetingStep === "received" && (
                    <div style={{ animation: "slideUp 0.3s ease-out", display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ color: "#fff", margin: 0, fontSize: "16px", fontWeight: "700" }}>Employee Portal: One-on-One Tasks</h4>
                        <span style={{ background: "#ffbd2e", color: "#000", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>PENDING ACTION</span>
                      </div>

                      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", padding: "16px", borderRadius: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        <div style={{ fontSize: "13px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)", display: "block", marginBottom: "2px" }}>Discussion Topic:</span>
                          <span style={{ color: "#fff", fontWeight: "600" }}>Q4 Roadmap & API Design Sync</span>
                        </div>
                        <div style={{ fontSize: "13px", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "8px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)", display: "block", marginBottom: "2px" }}>Assigned Task:</span>
                          <span style={{ color: "#fff", fontWeight: "600" }}>Complete database migration plan</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>
                          <span>Due: Nov 15, 2026</span>
                          <span style={{ color: "#ff5f56" }}>Remaining: 20 Days</span>
                        </div>
                      </div>

                      <button onClick={() => setMeetingStep("completed")} style={{ background: "#55FF60", color: "#000", border: "none", padding: "12px 24px", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "13px" }}>
                        Complete & Submit Action Item
                      </button>
                    </div>
                  )}

                  {/* Step 4: Employee Marks Task as Completed */}
                  {meetingStep === "completed" && (
                    <div style={{ animation: "slideUp 0.3s ease-out", display: "flex", flexDirection: "column", gap: "16px", textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ color: "#fff", margin: 0, fontSize: "15px", fontWeight: "700" }}>Task Status Updated</h4>
                        <span style={{ background: "rgba(85,255,96,0.1)", color: "#55FF60", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>COMPLETED ✓</span>
                      </div>

                      <div style={{ background: "rgba(85,255,96,0.04)", border: "1px dashed rgba(85,255,96,0.2)", padding: "24px", borderRadius: "12px" }}>
                        <h5 style={{ color: "#fff", fontSize: "15px", fontWeight: "700", margin: "0 0 8px" }}>Task Verified successfully</h5>
                        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", lineHeight: "1.6", margin: "0 0 16px" }}>
                          "Complete database migration plan" has been successfully verified. HR Manager Aditi Patil has been notified of completion.
                        </p>

                        <button onClick={() => handleTabChange("performance")} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "10px 20px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                          Schedule Another Meeting
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {activeTab === "payroll" && (
                <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px", animation: "slideUp 0.3s ease-out" }}>

                  {/* Step 1 & 2: Management Portal View */}
                  {(payrollStep === "upload" || payrollStep === "publishing") && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ color: "#fff", margin: 0, fontSize: "16px", fontWeight: "700" }}>Management Portal: Publish Payslips</h4>
                        <span style={{ fontSize: "12px", color: "#55FF60", fontWeight: "600" }}>Role: Admin / HR</span>
                      </div>

                      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "12px", padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)" }}>Select Employee:</span>
                          <span style={{ color: "#fff", fontWeight: "600" }}>Siya Sharma (Lead Engineer)</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)" }}>Pay Period:</span>
                          <span style={{ color: "#fff", fontWeight: "600" }}>October 2026</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "10px" }}>
                          <span style={{ color: "rgba(255,255,255,0.5)" }}>File Attachment:</span>
                          <span style={{ color: "#55FF60", fontWeight: "500" }}>📄 Payslip_Siya_Sharma_Oct2026.pdf</span>
                        </div>
                      </div>

                      {payrollStep === "upload" ? (
                        <button onClick={startPayrollSimulation} style={{ background: "#55FF60", color: "#000", border: "none", padding: "14px", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "14px", transition: "all 0.2s" }}>
                          Upload & Publish to Employee Portal
                        </button>
                      ) : (
                        <div style={{ textAlign: "center", padding: "10px" }}>
                          <div style={{ width: "100%", height: "4px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden", marginBottom: "8px" }}>
                            <div style={{ height: "100%", width: "100%", background: "#55FF60", borderRadius: "2px", animation: "loadingBar 2s infinite" }}></div>
                          </div>
                          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>Encrypting and pushing file to secure document vault...</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Step 3: Employee Inbox Notification */}
                  {payrollStep === "received" && (
                    <div style={{ animation: "slideUp 0.3s ease-out", display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ color: "#fff", margin: 0, fontSize: "16px", fontWeight: "700" }}>Employee Portal: Salary Slips</h4>
                        <span style={{ background: "#ff5f56", color: "#fff", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", animation: "pulse 1s infinite" }}>NEW DOCUMENT RECEIVED</span>
                      </div>

                      <div style={{ background: "rgba(85,255,96,0.05)", border: "1px dashed rgba(85,255,96,0.3)", padding: "20px", borderRadius: "12px", textAlign: "center" }}>
                        <p style={{ color: "#fff", fontSize: "14px", fontWeight: "600", margin: "0 0 4px" }}>
                          HR Management published a new document to your portal:
                        </p>
                        <span style={{ color: "#55FF60", fontSize: "15px", fontWeight: "700", display: "block", margin: "10px 0" }}>
                          📄 October_SalarySlip.pdf
                        </span>
                        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", display: "block", marginBottom: "12px" }}>
                          Uploaded just now by Admin.
                        </span>

                        <button onClick={() => setPayrollStep("viewing")} style={{ background: "#55FF60", color: "#000", border: "none", padding: "12px 24px", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "13px" }}>
                          Open & Verify Payslip
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Employee Opens Payslip Details */}
                  {payrollStep === "viewing" && (
                    <div style={{ animation: "slideUp 0.3s ease-out", display: "flex", flexDirection: "column", gap: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ color: "#fff", margin: 0, fontSize: "15px", fontWeight: "700" }}>📄 October_SalarySlip.pdf</h4>
                        <span style={{ color: "#55FF60", fontSize: "12px", fontWeight: "600" }}>Verified Secured ✓</span>
                      </div>

                      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", padding: "16px", borderRadius: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "rgba(255,255,255,0.5)", marginBottom: "6px" }}>
                          <span>Basic Earnings (Base + HRA):</span>
                          <span>$5,400.00</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "rgba(255,255,255,0.5)", marginBottom: "6px" }}>
                          <span>Special Allowance:</span>
                          <span>+$300.00</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "rgba(255,255,255,0.5)", marginBottom: "10px" }}>
                          <span>Deductions (Tax + PF):</span>
                          <span style={{ color: "#ff5f56" }}>-$200.00</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", fontWeight: "700", color: "#fff", paddingTop: "10px", borderTop: "1px dashed rgba(255,255,255,0.1)" }}>
                          <span>Net Disbursed Take-home:</span>
                          <span style={{ color: "#55FF60" }}>$5,500.00</span>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "10px" }}>
                        <button onClick={() => alert("Simulating Payslip PDF download successfully!")} style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: "8px", padding: "10px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                          📥 Download PDF
                        </button>
                        <button onClick={() => handleTabChange("payroll")} style={{ flex: 1, background: "transparent", border: "1px dashed rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)", borderRadius: "8px", padding: "10px", fontSize: "12px", cursor: "pointer" }}>
                          Upload New Payslip
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>




      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>

        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>Is GTELINQ suitable for businesses of all sizes?</h4>
            <p>
              Yes. GTELINQ is designed for startups and large enterprises with
              scalable HRMS features that grow alongside your organization.
            </p>
          </div>

          <div className="prod-faq-item">
            <h4>Can employees access GTELINQ from mobile devices?</h4>
            <p>
              Yes. Employees can securely access attendance, leave requests, payroll
              documents, and other HR services from supported mobile devices.
            </p>
          </div>

          <div className="prod-faq-item">
            <h4>Does GTELINQ support role-based access control?</h4>
            <p>
              Yes. GTELINQ provides secure role-based access for Employees, Managers, and
              Management, ensuring each user can only access the features and information
              relevant to their responsibilities.
            </p>
          </div>

        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
      {/* Video Modal */}
      {showVideo && createPortal(
        <div
          onClick={() => {
            setShowVideo(false);
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "100%" : "90%",
              maxWidth: "1000px",
              borderRadius: isMobile ? "12px" : "20px",
              overflow: "hidden",
              boxShadow: "0 20px 80px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                paddingBottom: "56.25%",
                height: 0,
              }}
            >
              <iframe
                width="560"
                height="315"
                src={youtubeEmbedUrl}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </div>


          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default GTELINQ;
